import React, { useEffect, useRef, useCallback } from "react";
import { MapPin, AlertCircle } from "lucide-react";

// Declare Google types
declare global {
  interface Window {
    google: any;
  }
  const google: any;
}

export interface AddressDetails {
  formattedAddress: string;
  address: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  lat?: number;
  lng?: number;
  placeId?: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string, details?: AddressDetails) => void;
  onSelect?: (details: AddressDetails) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  apiKey?: string;
  error?: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = "Enter site address",
  disabled = false,
  className = "",
  apiKey,
  error,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);

  // Check if Google Maps is loaded
  const checkGoogleMaps = useCallback(() => {
    return typeof google !== "undefined" && google.maps && google.maps.places;
  }, []);

  // Load Google Maps API if not already loaded
  useEffect(() => {
    if (checkGoogleMaps()) {
      return;
    }

    if (!apiKey) {
      console.error("Google Maps API key is required");
      return;
    }

    // Check if script is already loading
    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com/maps/api/js"]`
    );

    if (existingScript) {
      // Script is already loading or loaded
      return;
    }

    // Load the script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, [apiKey, checkGoogleMaps]);

  // Initialize Autocomplete widget
  useEffect(() => {
    if (!inputRef.current || autocompleteRef.current) return;

    const initAutocomplete = () => {
      if (!checkGoogleMaps() || !inputRef.current) return;

      try {
        // Create autocomplete widget
        autocompleteRef.current = new google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ["address"],
            fields: [
              "formatted_address",
              "address_components",
              "geometry",
              "place_id",
            ],
          }
        );

        // Listen for place selection
        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current.getPlace();

          if (!place.geometry) {
            // User entered a name that was not suggested
            console.log("No details available for input: '" + place.name + "'");
            return;
          }

          // Parse address components
          const addressComponents = place.address_components || [];
          const details: AddressDetails = {
            formattedAddress: place.formatted_address || "",
            address: place.formatted_address || "",
            placeId: place.place_id,
            lat: place.geometry?.location?.lat(),
            lng: place.geometry?.location?.lng(),
          };

          // Extract address components
          addressComponents.forEach((component: any) => {
            const types = component.types;

            if (types.includes("locality")) {
              details.city = component.long_name;
            } else if (types.includes("administrative_area_level_1")) {
              details.state = component.short_name;
            } else if (types.includes("country")) {
              details.country = component.long_name;
            } else if (types.includes("postal_code")) {
              details.postalCode = component.long_name;
            }
          });

          // Update the input value
          onChange(details.formattedAddress, details);

          // Call onSelect callback
          if (onSelect) {
            onSelect(details);
          }
        });
      } catch (err) {
        console.error("Failed to initialize autocomplete:", err);
      }
    };

    // Try to initialize immediately if Google is loaded
    if (checkGoogleMaps()) {
      initAutocomplete();
    } else {
      // Wait for Google Maps to load
      const checkInterval = setInterval(() => {
        if (checkGoogleMaps()) {
          initAutocomplete();
          clearInterval(checkInterval);
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);

      return () => clearInterval(checkInterval);
    }
  }, [checkGoogleMaps, onChange, onSelect]);

  // Handle manual input changes (when user types without selecting)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          disabled={disabled}
          placeholder={placeholder}
          className={`w-full px-3 py-2 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 transition-colors ${
            error ? "border-red-300 focus:ring-red-500" : "border-gray-300"
          } ${className}`}
          autoComplete="off"
        />

        {/* Icon */}
        <div className="absolute left-3 top-2.5 pointer-events-none">
          <MapPin className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mt-1 text-sm text-red-600 flex items-center space-x-1">
          <AlertCircle className="w-3 h-3" />
          <span>{error}</span>
        </div>
      )}

      {/* Helper text */}
      {!error && (
        <p className="mt-1 text-xs text-gray-500">
          Start typing to see address suggestions
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
